import { FeedEventPayload, FeedItem } from '@knocklabs/client';
import { useKnockFeed } from '@knocklabs/react';
import { useEffect, useRef } from 'react';
import { MessageDescriptor, defineMessage, useIntl } from 'react-intl';
import {
  getNotificationItemBodyPlainText,
  getNotificationItemContent,
} from '../shared/get-notification-item-content';
import { getNotificationActionUrl } from '../shared/notification-action-url';
import {
  getNotificationSeverity,
  shouldShowNotificationToast,
} from '../shared/notification-severity';
import { getNotificationWorkflow, notificationWorkflowCategoryMessages } from '../workflows';
import { showNotificationToast } from './show-notification-toast';

const fallbackTitleMessage = defineMessage({
  id: 'notifications.item.fallbackTitle',
  defaultMessage: 'Notificación',
});

const viewLinkMessage = defineMessage({
  id: 'notifications.toast.viewLink',
  defaultMessage: 'Ver',
});

/**
 * Renders one realtime feed item as a ribbon toast matching the in-app feed severity mark.
 * Title/body use the same Knock-markdown split as the feed list.
 * Eyebrow shows the workflow category (product area), not the preference row title.
 */
function showItemToast(
  item: FeedItem,
  formatMessage: (descriptor: MessageDescriptor) => string,
): void {
  const fallbackTitle = formatMessage(fallbackTitleMessage);
  const { title, bodyHtml } = getNotificationItemContent(item, fallbackTitle);
  const severity = getNotificationSeverity(item.source?.key);
  const workflow = getNotificationWorkflow(item.source?.key);
  if (!shouldShowNotificationToast(workflow?.toast, severity)) {
    return;
  }
  const eyebrow = workflow
    ? formatMessage(notificationWorkflowCategoryMessages[workflow.category])
    : null;
  const actionUrl = getNotificationActionUrl(item);

  showNotificationToast({
    title,
    eyebrow,
    body: getNotificationItemBodyPlainText(bodyHtml),
    severity,
    actionLabel: actionUrl ? formatMessage(viewLinkMessage) : null,
    actionUrl,
  });
}

/**
 * Shows a ribbon toast for each item that arrives over the Knock realtime feed
 * (not the initial page fetch).
 */
export function useNotificationToasts() {
  const { feedClient } = useKnockFeed();
  const { formatMessage } = useIntl();
  const formatMessageRef = useRef(formatMessage);

  formatMessageRef.current = formatMessage;

  useEffect(() => {
    const handleRealtime = (payload: FeedEventPayload) => {
      for (const item of payload.items) {
        showItemToast(item, formatMessageRef.current);
      }
    };

    feedClient.on('items.received.realtime', handleRealtime);
    return () => {
      feedClient.off('items.received.realtime', handleRealtime);
    };
  }, [feedClient]);
}
